import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KeyboardNavigationContext } from './KeyboardNavigationContext';
import { sortByDomPosition } from './helpers';
import type { KeyboardNavigationProviderProps, NavigableItem } from './types';

export function KeyboardNavigationProvider({
	children,
	onEscape,
	onDefaultEnter,
	onAfterSelect,
}: KeyboardNavigationProviderProps) {
	const [activeItemId, setActiveItemId] = useState<string | null>(null);
	const [navigableItemsCount, setNavigableItemsCount] = useState(0); // Beware: this should always be kept in sync with the registry ref
	const registryRef = useRef(new Map<string, NavigableItem>());
	// Open nested menus (e.g. a Dropdown) register here to suspend our own key handling.
	const suspendedByRef = useRef(new Set<string>());

	const setIsNavigationSuspended = useCallback((id: string, isSuspended: boolean) => {
		if (isSuspended) {
			suspendedByRef.current.add(id);
		} else {
			suspendedByRef.current.delete(id);
		}
	}, []);

	const registerNavigableItem = useCallback((id: string, item: NavigableItem) => {
		registryRef.current.set(id, item);
		setNavigableItemsCount(registryRef.current.size);
	}, []);

	const unregisterNavigableItem = useCallback((id: string) => {
		registryRef.current.delete(id);
		setNavigableItemsCount(registryRef.current.size);
		setActiveItemId((prev) => (prev === id ? null : prev));
	}, []);

	useEffect(() => {
		if (!activeItemId) {
			return;
		}

		const navigableItem = registryRef.current.get(activeItemId);
		navigableItem?.ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}, [activeItemId]);

	const getOrderedNavigableItems = useCallback(() => {
		return sortByDomPosition(Array.from(registryRef.current.entries()));
	}, []);

	const handleArrowNavigation = useCallback(
		(direction: 'up' | 'down') => {
			const orderedNavigableItems = getOrderedNavigableItems();

			if (orderedNavigableItems.length === 0) {
				return;
			}

			const currentIndex = orderedNavigableItems.findIndex(([id]) => id === activeItemId);

			if (currentIndex === -1) {
				const nextIndex = direction === 'down' ? 0 : orderedNavigableItems.length - 1;
				setActiveItemId(orderedNavigableItems[nextIndex][0]);
			} else {
				const nextIndex = currentIndex + (direction === 'down' ? 1 : -1);
				const isOutOfBounds = nextIndex < 0 || nextIndex >= orderedNavigableItems.length;
				setActiveItemId(isOutOfBounds ? null : orderedNavigableItems[nextIndex][0]);
			}
		},
		[activeItemId, getOrderedNavigableItems],
	);

	// Snapshot on keydown: a nested menu can close itself (and unsuspend) before our
	// keyup handler for that same press runs, so keyup must not trust the live value.
	const wasSuspendedOnKeyDownRef = useRef(false);

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		const isSuspended = suspendedByRef.current.size > 0;
		wasSuspendedOnKeyDownRef.current = isSuspended;

		// Prevent here, not on keyup — otherwise a focused non-input element (e.g. a
		// dropdown trigger button) scrolls the page on Arrow before keyup ever runs.
		if (
			!isSuspended &&
			(e.key === 'ArrowDown' ||
				e.key === 'ArrowUp' ||
				e.key === 'Enter' ||
				e.key === 'Escape')
		) {
			e.preventDefault();
		}
	}, []);

	const handleKeyUp = useCallback(
		(e: React.KeyboardEvent) => {
			if (wasSuspendedOnKeyDownRef.current) {
				return;
			}

			switch (e.key) {
				case 'ArrowDown':
					e.stopPropagation();
					e.preventDefault();
					handleArrowNavigation('down');
					break;
				case 'ArrowUp':
					e.stopPropagation();
					e.preventDefault();
					handleArrowNavigation('up');
					break;
				case 'Enter': {
					e.stopPropagation();
					e.preventDefault();

					if (activeItemId) {
						registryRef.current.get(activeItemId)?.select();
						setActiveItemId(null);
					} else {
						onDefaultEnter();
					}

					break;
				}
				case 'Escape':
					e.stopPropagation();
					e.preventDefault();
					setActiveItemId(null);
					onEscape();
					break;
				default:
					break;
			}
		},
		[handleArrowNavigation, activeItemId, onDefaultEnter, onEscape],
	);

	const contextValue = useMemo(
		() => ({
			activeItemId,
			navigableItemsCount,
			registerNavigableItem,
			unregisterNavigableItem,
			setIsNavigationSuspended,
			onAfterSelect,
		}),
		[
			activeItemId,
			navigableItemsCount,
			registerNavigableItem,
			unregisterNavigableItem,
			setIsNavigationSuspended,
			onAfterSelect,
		],
	);

	return (
		<KeyboardNavigationContext.Provider value={contextValue}>
			{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
			<div onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
				{children}
			</div>
		</KeyboardNavigationContext.Provider>
	);
}
